// useSearchBuy — what makes a Search row's Buy the lists' Buy. MONEY PATH.
// The header calls it and hands its two props to the modal, the way Discover
// hands its table `onGuestBuy` / `onBuy`:
//   - a guest: onGuestBuy, the login prompt — no page opens, nothing is armed;
//   - signed in: onBuy — lib/listBuy, the one function Discover's rows call,
//     with the modal's own Quick buy amount and trading preset.
//
// The buy's function is NOT imported here. Its balance gate imports the trade
// client, and this hook is in the app's boot graph (the header): the client
// must not be reachable from the entry (982f1504). The function is loaded when
// the modal first opens for a signed-in user. Until it has arrived there is no
// onBuy — and the modal is told why, `buyLoad`: 'loading' (a row's Buy waits
// for it) or 'failed' (a row's Buy says so, and opens the token with nothing
// armed). A pill that says "Buy 2" never opens the page as if nothing had been
// asked (review of round 5). Opening the modal again asks for the load again.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useTradeLifecycleToast } from '../../hooks/useTradeLifecycleToast'
import { searchPairPath } from '../../lib/pairRoutes'
import { useOptionalTradingSession } from '../../wallet/TradingSessionContext'
import { useGuestTradePrompt } from '../../wallet/guestTradePrompt'
import { usePixelToast } from '../ui/pixel-toast'

type ListBuyModule = typeof import('../../lib/listBuy')

/** What a row hands to its Buy: where it opens (searchPairPath) and what the
 *  toasts and the balance gate need of it. */
export interface SearchBuyRow {
  master: string
  pool: string
  ticker: string
  dex?: string
  quoteToken?: string | null
  launchpadSource?: string | null
  launchpadStatus?: string | null
}

/** `preset`: the index of one of the trade form's presets, 0 for the first.
 *  `pressedAt`: when the row's Buy was pressed (Date.now() then) — the buy's
 *  deadline counts from it, also for a press that waited for this function. */
export type SearchBuy = (
  row: SearchBuyRow,
  amount: string,
  preset: number,
  pressedAt: number
) => void

export interface SearchBuyProps {
  onGuestBuy?: () => void
  onBuy?: SearchBuy
  /** Signed in, and no onBuy yet: its function is on its way ('loading'), or
   *  did not load ('failed'). Neither this nor a buy: no buy on the surface. */
  buyLoad?: 'loading' | 'failed'
}

/** `open`: the modal is open — a signed-in user's buy function is loaded then. */
export function useSearchBuy(open: boolean): SearchBuyProps {
  const { guest, prompt } = useGuestTradePrompt()
  const navigate = useNavigate()
  const tradeToast = useTradeLifecycleToast()
  const pixelToast = usePixelToast()
  const session = useOptionalTradingSession()
  const nativeBalanceNano = session?.liveBalance.balanceNano ?? null
  // MONEY PATH: the wallet a buy is pressed with (it goes out from no other)
  const wallet = session?.tradeRecipient?.address ?? null
  const [listBuyModule, setListBuyModule] = useState<ListBuyModule | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)

  const wanted = open && !guest && listBuyModule == null
  useEffect(() => {
    if (!wanted) return
    let alive = true
    setLoadFailed(false)
    import('../../lib/listBuy').then(
      (loaded) => {
        if (alive) setListBuyModule(loaded)
      },
      () => {
        // not loaded: no onBuy — a row's Buy says so (buyLoad: 'failed')
        if (alive) setLoadFailed(true)
      }
    )
    return () => {
      alive = false
    }
  }, [wanted])

  if (guest) return { onGuestBuy: prompt }
  if (!listBuyModule) return { buyLoad: loadFailed ? 'failed' : 'loading' }
  const { listBuy, listBuyVenue } = listBuyModule
  return {
    onBuy: (row, amount, preset, pressedAt) =>
      listBuy(
        { navigate, tradeToast, pixelToast, nativeBalanceNano, wallet },
        {
          path: searchPairPath(row),
          poolHint: row.pool,
          ticker: row.ticker,
          venue: listBuyVenue({
            dex: row.dex,
            launchpad_source: row.launchpadSource,
            launchpad_status: row.launchpadStatus,
          }),
        },
        amount,
        preset,
        pressedAt
      ),
  }
}
